import { useState } from 'react';
import { Box, Button, ButtonGroup, Chip, Collapse, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { NavLink, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { EntityType } from './types';
import { CONTENT, getEntityType } from './utils';

import { useMember } from 'modules/common/queries/member';
import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import ChevronUpIcon from 'modules/common/components/Icons/ChevronUpIcon';
import DownloadIcon from 'modules/common/components/Icons/DownloadIcon';
import ChartBarIcon from 'modules/common/components/Icons/ChartBarIcon';
import ReceiptIcon from 'modules/common/components/Icons/ReceiptIcon';
import CashIcon from 'modules/common/components/Icons/CashIcon';
import DocumentCheckIcon from 'modules/common/components/Icons/DocumentCheckIcon';

/**
 * Demo replacement for /bookkeeping/reports/*. Prod renders Hurdlr-shaped JSON through
 * JsonReportRenderer, which is blank in the demo (no Hurdlr session). This component
 * provides the same five-tab navigation with statically rendered P&L and Balance Sheet
 * mirroring the prod layout. All / P&L / Balance Sheet have designed states; the other
 * two render a brief placeholder.
 */

type Period = 'lastYear' | 'thisYear' | 'lastQuarter' | 'thisQuarter';
const PERIOD_LABEL: Record<Period, string> = {
    lastYear: 'Last year',
    thisYear: 'This year',
    lastQuarter: 'Last quarter',
    thisQuarter: 'This quarter',
};

interface LineItem {
    name: string;
    amount: number;
}

const fmt = (value: number) => {
    const sign = value < 0 ? '-' : '';
    const absolute = Math.abs(value);
    return `${sign}$${absolute.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function sumItems(items: LineItem[]): number {
    return items.reduce((s, i) => s + i.amount, 0);
}

/**
 * Per-profile P&L expense / revenue lines. Scaled to match each profile's ytd totals
 * from the dashboard blob, so the demo Reports tab is internally consistent with
 * what shows on the Accounting Overview.
 */
function getProfitLoss(entityType: EntityType): {
    revenue: LineItem[];
    expenses: LineItem[];
    reimbursement: LineItem[];
} {
    // Same consultant persona across entity types: ~$160K revenue, ~$38.5K expenses. The
    // reimbursement line only applies to S-Corp (accountable plan); LLC members can't run
    // reimbursements the same way.
    const revenue: LineItem[] = [
        { name: 'Consulting Income', amount: 135000 },
        { name: 'Project Fees', amount: 25000 },
    ];
    const expenses: LineItem[] = [
        { name: 'Software & Subscriptions', amount: 11500 },
        { name: 'Travel (excl. Meals)', amount: 9800 },
        { name: 'Legal & Professional Services', amount: 7800 },
        { name: 'Meals 50%', amount: 4200 },
        { name: 'Office Expense', amount: 3400 },
        { name: 'Contractors', amount: 1100 },
        { name: 'Equipment & Furniture', amount: 850 },
        { name: 'Bank Charges & Fees', amount: 350 },
    ];
    const reimbursement: LineItem[] =
        entityType === 'scorp' ? [{ name: 'Home Office', amount: 3315 }] : [{ name: 'Home Office', amount: 0 }];
    return { revenue, expenses, reimbursement };
}

interface BalanceSheetData {
    currentAssets: LineItem[];
    fixedAssets: LineItem[];
    currentLiabilities: LineItem[];
    longTermLiabilities: LineItem[];
    equity: LineItem[];
}

/**
 * Cash flow lines per profile. Derived from each profile's P&L net income, then layered
 * with realistic changes in working capital and financing activity. The "operating"
 * total + "financing" total nets out to the period's net cash change shown at the bottom.
 */
function getCashFlow(entityType: EntityType): { operating: LineItem[]; financing: LineItem[] } {
    // Net income identical across entity types ($121,500). Financing varies because of
    // structural differences: S-Corp distributes via shareholder distribution + reasonable
    // comp; LLC uses owner draws.
    const operatingBase: LineItem[] = [
        { name: 'Net Income', amount: 121500 },
        { name: 'Accounts Receivable', amount: -2150 },
        { name: 'Wells Fargo Credit Card *3782', amount: 1840 },
    ];
    if (entityType === 'scorp') {
        return {
            operating: [
                ...operatingBase,
                { name: 'Other Current Assets', amount: -640 },
                { name: 'Accountable Plan Clearing', amount: 240 },
            ],
            financing: [
                { name: 'Additional Paid-in Capital', amount: 12000 },
                { name: 'Shareholder Distribution', amount: -42000 },
            ],
        };
    }
    // LLC
    return {
        operating: operatingBase,
        financing: [
            { name: 'Additional Paid-in Capital', amount: 8000 },
            { name: 'Owner Draw', amount: -85000 },
        ],
    };
}

function getBalanceSheet(entityType: EntityType): BalanceSheetData {
    // Same consultant persona across entity types — current income identical at $121,500.
    // Cash positions vary slightly by entity (S-Corp keeps more in business accounts; LLC
    // drains more via owner draws), and the equity composition differs because S-Corp
    // distributes via shareholder distribution + reasonable comp.
    const currentAssetsCommon: LineItem[] = [
        { name: 'Wells Fargo Business Checking *0378', amount: 32400 },
        { name: 'Accounts Receivable', amount: 12750 },
    ];
    const fixedAssets: LineItem[] = [{ name: 'Computers, Servers and Related Hardware', amount: 2200 }];
    const liabilitiesCommon: LineItem[] = [{ name: 'Wells Fargo Credit Card *3782', amount: 1840 }];

    if (entityType === 'llc') {
        return {
            currentAssets: [...currentAssetsCommon, { name: 'PayPal', amount: 4800 }],
            fixedAssets,
            currentLiabilities: liabilitiesCommon,
            longTermLiabilities: [],
            equity: [
                { name: 'Current Income', amount: 121500 },
                { name: 'Additional Paid-in Capital', amount: 8000 },
                { name: 'Owner Draw', amount: -85000 },
            ],
        };
    }
    return {
        currentAssets: [
            ...currentAssetsCommon,
            { name: 'Chase Business Savings *9385', amount: 24500 },
            { name: 'PayPal', amount: 4800 },
            { name: 'Other Current Assets', amount: 1800 },
        ],
        fixedAssets,
        currentLiabilities: [
            ...liabilitiesCommon,
            { name: 'Accountable Plan Clearing', amount: 240 },
            { name: 'Sales Tax Payable', amount: 120 },
        ],
        longTermLiabilities: [],
        equity: [
            { name: 'Current Income', amount: 121500 },
            { name: 'Additional Paid-in Capital', amount: 12000 },
            { name: 'Shareholder Distribution', amount: -42000 },
            { name: 'Reasonable Compensation', amount: -60000 },
        ],
    };
}

/* --------------------------------- atoms --------------------------------- */

function PeriodMenu({ value, onChange }: { value: Period; onChange: (p: Period) => void }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={open ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                sx={{ color: 'text.primary', borderColor: 'common.black' }}>
                {PERIOD_LABEL[value]}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {(Object.keys(PERIOD_LABEL) as Period[]).map((p) => (
                    <MenuItem
                        key={p}
                        selected={p === value}
                        onClick={() => {
                            onChange(p);
                            setAnchorEl(null);
                        }}>
                        {PERIOD_LABEL[p]}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

function ReportHeader({ period, onPeriodChange }: { period: Period; onPeriodChange: (p: Period) => void }) {
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}>
            <PeriodMenu value={period} onChange={onPeriodChange} />
            <Button
                variant="outlined"
                color="primary"
                startIcon={<DownloadIcon fontSize="small" />}
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                Download
            </Button>
        </Stack>
    );
}

function periodToRange(period: Period): { begin: dayjs.Dayjs; end: dayjs.Dayjs } {
    const now = dayjs();
    if (period === 'lastYear') {
        return { begin: now.subtract(1, 'year').startOf('year'), end: now.subtract(1, 'year').endOf('year') };
    }
    if (period === 'thisYear') {
        return { begin: now.startOf('year'), end: now.endOf('year') };
    }
    // Quarters: compute from current month, no dayjs quarter plugin needed.
    const monthIdx = now.month();
    const quarterStartMonth = Math.floor(monthIdx / 3) * 3;
    const thisQuarterStart = now.month(quarterStartMonth).startOf('month');
    const thisQuarterEnd = now.month(quarterStartMonth + 2).endOf('month');
    if (period === 'lastQuarter') {
        return {
            begin: thisQuarterStart.subtract(3, 'month'),
            end: thisQuarterStart.subtract(1, 'day'),
        };
    }
    return { begin: thisQuarterStart, end: thisQuarterEnd };
}

function ReportTitle({ businessName, title, period }: { businessName: string; title: string; period: Period }) {
    const { begin, end } = periodToRange(period);
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
            <Box>
                <Typography variant="body2" fontWeight={600}>
                    {businessName}
                </Typography>
                <Typography variant="h4" sx={{ mt: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Monthly | {begin.format('MM/DD/YYYY')} - {end.format('MM/DD/YYYY')}
                </Typography>
            </Box>
            <Chip
                label="CONFIDENTIAL"
                size="small"
                sx={{
                    height: 24,
                    bgcolor: 'primary.lightHover',
                    color: 'primary.main',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                }}
            />
        </Stack>
    );
}

function SectionHeader({ label }: { label: string }) {
    return (
        <Box sx={{ bgcolor: 'cement.100', px: 2, py: 1.25 }}>
            <Typography variant="body2" fontWeight={700}>
                {label}
            </Typography>
        </Box>
    );
}

function SubsectionHeader({ label }: { label: string }) {
    return (
        <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Box>
    );
}

function Row({ label, amount, bold = false }: { label: string; amount: number; bold?: boolean }) {
    const negative = amount < 0;
    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
                px: 2,
                py: 1.25,
                borderTop: 1,
                borderColor: 'divider',
            }}>
            <Typography variant="body2" fontWeight={bold ? 700 : 400}>
                {label}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={bold ? 700 : 400}
                color={negative ? 'error.main' : 'text.primary'}
                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmt(amount)}
            </Typography>
        </Stack>
    );
}

function Disclaimer() {
    return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            These financial statements have not been subjected to an audit or review or compilation engagement, and no
            assurance is provided on them.
        </Typography>
    );
}

/* ---------------------------- tab: Profit & Loss --------------------------- */

function ProfitLossView({ businessName, entityType }: { businessName: string; entityType: EntityType }) {
    const [period, setPeriod] = useState<Period>('lastYear');
    const data = getProfitLoss(entityType);
    const totalRevenue = sumItems(data.revenue);
    const totalReimbursement = sumItems(data.reimbursement);
    const totalExpenses = sumItems(data.expenses) + totalReimbursement;
    const netIncome = totalRevenue - totalExpenses;

    return (
        <Box>
            <ReportHeader period={period} onPeriodChange={setPeriod} />
            <ReportTitle businessName={businessName} title="Profit & Loss" period={period} />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <SectionHeader label="Revenue" />
                {data.revenue.map((r) => (
                    <Row key={r.name} label={r.name} amount={r.amount} />
                ))}
                <Row label="Total Revenue" amount={totalRevenue} bold />

                <SectionHeader label="Expenses" />
                {data.expenses.map((e) => (
                    <Row key={e.name} label={e.name} amount={e.amount} />
                ))}
                {data.reimbursement.length > 0 && (
                    <>
                        <SubsectionHeader label="Employee Reimbursement" />
                        {data.reimbursement.map((r) => (
                            <Row key={r.name} label={r.name} amount={r.amount} />
                        ))}
                    </>
                )}
                <Row label="Total Expenses" amount={totalExpenses} bold />

                <Box sx={{ height: 24 }} />
                <Row label="Net Income" amount={netIncome} bold />
            </Box>
            <Disclaimer />
        </Box>
    );
}

/* ---------------------------- tab: Balance Sheet --------------------------- */

function BalanceSheetView({ businessName, entityType }: { businessName: string; entityType: EntityType }) {
    const [period, setPeriod] = useState<Period>('lastYear');
    const data = getBalanceSheet(entityType);

    const totalCurrentAssets = sumItems(data.currentAssets);
    const totalFixedAssets = sumItems(data.fixedAssets);
    const totalAssets = totalCurrentAssets + totalFixedAssets;
    const totalCurrentLiabilities = sumItems(data.currentLiabilities);
    const totalLongTermLiabilities = sumItems(data.longTermLiabilities);
    const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;
    const totalEquity = sumItems(data.equity);
    const totalLiabilitiesAndEquity = totalLiabilities + totalEquity;

    return (
        <Box>
            <ReportHeader period={period} onPeriodChange={setPeriod} />
            <ReportTitle businessName={businessName} title="Balance Sheet" period={period} />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <SectionHeader label="Assets" />
                <SubsectionHeader label="Current Assets" />
                {data.currentAssets.map((a) => (
                    <Row key={a.name} label={a.name} amount={a.amount} />
                ))}
                <Row label="Total Current Assets" amount={totalCurrentAssets} bold />
                {data.fixedAssets.length > 0 && (
                    <>
                        <SubsectionHeader label="Fixed Assets" />
                        {data.fixedAssets.map((a) => (
                            <Row key={a.name} label={a.name} amount={a.amount} />
                        ))}
                        <Row label="Total Fixed Assets" amount={totalFixedAssets} bold />
                    </>
                )}
                <Row label="Total Assets" amount={totalAssets} bold />

                <SectionHeader label="Liabilities" />
                <SubsectionHeader label="Current Liabilities" />
                {data.currentLiabilities.map((l) => (
                    <Row key={l.name} label={l.name} amount={l.amount} />
                ))}
                <Row label="Total Current Liabilities" amount={totalCurrentLiabilities} bold />
                {data.longTermLiabilities.length > 0 && (
                    <>
                        <SubsectionHeader label="Long-term Liabilities" />
                        {data.longTermLiabilities.map((l) => (
                            <Row key={l.name} label={l.name} amount={l.amount} />
                        ))}
                        <Row label="Total Long-term Liabilities" amount={totalLongTermLiabilities} bold />
                    </>
                )}
                <Row label="Total Liabilities" amount={totalLiabilities} bold />

                <SectionHeader label="Equity" />
                {data.equity.map((e) => (
                    <Row key={e.name} label={e.name} amount={e.amount} />
                ))}
                <Row label="Total equity" amount={totalEquity} bold />

                <Box sx={{ height: 24 }} />
                <Row label="Total liabilities and equity" amount={totalLiabilitiesAndEquity} bold />
            </Box>
            <Disclaimer />
        </Box>
    );
}

/* ------------------------ tab: Cash Flow Statement ------------------------ */

function CashFlowView({ businessName, entityType }: { businessName: string; entityType: EntityType }) {
    const [period, setPeriod] = useState<Period>('lastYear');
    const data = getCashFlow(entityType);
    const operatingTotal = sumItems(data.operating);
    const financingTotal = sumItems(data.financing);
    const netChange = operatingTotal + financingTotal;

    return (
        <Box>
            <ReportHeader period={period} onPeriodChange={setPeriod} />
            <ReportTitle businessName={businessName} title="Cash Flow Statement" period={period} />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <SectionHeader label="Cash flows from operating activities" />
                {data.operating.map((i) => (
                    <Row key={i.name} label={i.name} amount={i.amount} />
                ))}
                <Row label="Net cash provided by operating activities" amount={operatingTotal} bold />

                <SectionHeader label="Cash flows from financing activities" />
                {data.financing.map((i) => (
                    <Row key={i.name} label={i.name} amount={i.amount} />
                ))}
                <Row
                    label={
                        financingTotal >= 0
                            ? 'Net cash provided by financing activities'
                            : 'Net cash used in financing activities'
                    }
                    amount={financingTotal}
                    bold
                />

                <Box sx={{ height: 24 }} />
                <Row label="Net increase (decrease) in cash" amount={netChange} bold />
            </Box>
            <Disclaimer />
        </Box>
    );
}

/* --------------------------- tab: Trial Balance --------------------------- */

interface TrialRow {
    name: string;
    debit: number;
    credit: number;
}

/**
 * Trial Balance is derived from the existing Balance Sheet + P&L data, so any edit to
 * the P&L or BS auto-flows here. Normal-balance convention: assets and expenses sit in
 * the debit column; liabilities, equity, and revenue sit in the credit column. Negative
 * amounts (e.g. shareholder distributions on equity) flip to the opposite column.
 */
function toTrialRow(item: LineItem, normal: 'debit' | 'credit'): TrialRow {
    if (normal === 'debit') {
        return { name: item.name, debit: Math.max(0, item.amount), credit: Math.max(0, -item.amount) };
    }
    return { name: item.name, debit: Math.max(0, -item.amount), credit: Math.max(0, item.amount) };
}

function getTrialBalance(entityType: EntityType): Record<string, TrialRow[]> {
    const bs = getBalanceSheet(entityType);
    const pl = getProfitLoss(entityType);
    return {
        Assets: [...bs.currentAssets, ...bs.fixedAssets].map((i) => toTrialRow(i, 'debit')),
        Liabilities: [...bs.currentLiabilities, ...bs.longTermLiabilities].map((i) => toTrialRow(i, 'credit')),
        Equity: bs.equity.map((i) => toTrialRow(i, 'credit')),
        Revenue: pl.revenue.map((i) => toTrialRow(i, 'credit')),
        Expenses: [...pl.expenses, ...pl.reimbursement].map((i) => toTrialRow(i, 'debit')),
    };
}

const DASH = '—';

function TrialRowView({ row, bold = false }: { row: TrialRow; bold?: boolean }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 120px',
                alignItems: 'center',
                px: 2,
                py: 1.25,
                borderTop: 1,
                borderColor: 'divider',
                columnGap: 2,
            }}>
            <Typography variant="body2" fontWeight={bold ? 700 : 400}>
                {row.name}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={bold ? 700 : 400}
                sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                color={row.debit === 0 ? 'text.secondary' : 'text.primary'}>
                {row.debit === 0 ? DASH : fmt(row.debit)}
            </Typography>
            <Typography
                variant="body2"
                fontWeight={bold ? 700 : 400}
                sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                color={row.credit === 0 ? 'text.secondary' : 'text.primary'}>
                {row.credit === 0 ? DASH : fmt(row.credit)}
            </Typography>
        </Box>
    );
}

function TrialColumnHeader() {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 120px 120px',
                alignItems: 'center',
                px: 2,
                py: 1,
                columnGap: 2,
            }}>
            <span />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', letterSpacing: '0.06em' }}>
                DEBIT
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', letterSpacing: '0.06em' }}>
                CREDIT
            </Typography>
        </Box>
    );
}

function TrialBalanceView({ businessName, entityType }: { businessName: string; entityType: EntityType }) {
    const [period, setPeriod] = useState<Period>('lastYear');
    const sections = getTrialBalance(entityType);
    const sectionTotals = (rows: TrialRow[]): TrialRow => ({
        name: 'Total',
        debit: rows.reduce((s, r) => s + r.debit, 0),
        credit: rows.reduce((s, r) => s + r.credit, 0),
    });

    return (
        <Box>
            <ReportHeader period={period} onPeriodChange={setPeriod} />
            <ReportTitle businessName={businessName} title="Trial Balance" period={period} />
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <TrialColumnHeader />
                {Object.entries(sections).map(([name, rows]) => (
                    <Box key={name}>
                        <SectionHeader label={name} />
                        {rows.map((r) => (
                            <TrialRowView key={r.name} row={r} />
                        ))}
                        <TrialRowView row={{ ...sectionTotals(rows), name: `Total ${name}` }} bold />
                    </Box>
                ))}
            </Box>
            <Disclaimer />
        </Box>
    );
}

/* ----------------------------- tab: All ----------------------------- */

interface AllReportRow {
    name: string;
    flag?: string; // (SPREADSHEET) etc
    cta: 'View' | 'Download';
}

interface AllReportCategory {
    title: string;
    icon: React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;
    rows: AllReportRow[];
}

const ALL_CATEGORIES: AllReportCategory[] = [
    {
        title: 'Expense Reports',
        icon: ReceiptIcon,
        rows: [
            { name: 'Business Expenses by Business', cta: 'View' },
            { name: 'Business Expenses by Category', cta: 'View' },
            { name: 'Business Expenses by Vendor', cta: 'View' },
            { name: 'Business Expenses by Client', cta: 'View' },
            { name: 'Expense Details List with Receipts', flag: 'SPREADSHEET', cta: 'Download' },
            { name: 'Personal Expenses by Category', cta: 'View' },
            { name: 'Personal Expenses by Vendor', cta: 'View' },
            { name: 'Total Expenses from Recurring Vendors', cta: 'View' },
        ],
    },
    {
        title: 'Income Reports',
        icon: CashIcon,
        rows: [
            { name: 'Revenue by Source', cta: 'View' },
            { name: 'Revenue by Client', cta: 'View' },
            { name: 'Invoiced vs. Collected', cta: 'View' },
            { name: 'Income Details List', flag: 'SPREADSHEET', cta: 'Download' },
        ],
    },
    {
        title: 'Reconciliation Reports',
        icon: DocumentCheckIcon,
        rows: [
            { name: 'Bank Reconciliation Summary', cta: 'View' },
            { name: 'Bank Reconciliation Detail', cta: 'View' },
            { name: 'Uncategorized Transactions', cta: 'View' },
        ],
    },
];

// Other actions available behind the chevron dropdown — depends on the primary CTA.
const ROW_DROPDOWN_ACTIONS: Record<AllReportRow['cta'], string[]> = {
    View: ['Download', 'Email Report'],
    Download: ['Email Report'],
};

function AllReportsRow({ row }: { row: AllReportRow }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const otherActions = ROW_DROPDOWN_ACTIONS[row.cta];
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
            sx={{
                px: 2,
                py: 2,
                borderTop: 1,
                borderColor: 'divider',
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flexGrow: 1 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>
                    <ChartBarIcon fontSize="small" />
                </Box>
                <Typography variant="body1">
                    {row.name}{' '}
                    {row.flag && (
                        <Typography component="span" variant="caption" color="text.secondary">
                            ({row.flag})
                        </Typography>
                    )}
                </Typography>
            </Stack>
            <ButtonGroup variant="outlined" color="primary" size="small">
                <Button sx={{ minWidth: 96 }}>{row.cta}</Button>
                <Button
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{ px: 0.5, minWidth: 32 }}
                    aria-label={`More ${row.cta.toLowerCase()} actions`}>
                    {open ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
                </Button>
            </ButtonGroup>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                {otherActions.map((action) => (
                    <MenuItem key={action} onClick={() => setAnchorEl(null)}>
                        {action}
                    </MenuItem>
                ))}
            </Menu>
        </Stack>
    );
}

function AllReportsCategory({ category, defaultOpen = false }: { category: AllReportCategory; defaultOpen?: boolean }) {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = category.icon;
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen((v) => !v);
                    }
                }}
                sx={{
                    px: 2,
                    py: 2,
                    cursor: 'pointer',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: 'action.hover' },
                }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Icon fontSize="small" />
                    <Typography variant="h6">{category.title}</Typography>
                </Stack>
                {open ? <ChevronUpIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />}
            </Stack>
            <Collapse in={open}>
                <Box>
                    {category.rows.map((r) => (
                        <AllReportsRow key={r.name} row={r} />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
}

function AllView() {
    return (
        <Stack spacing={2} sx={{ mt: 1 }}>
            {ALL_CATEGORIES.map((c, i) => (
                <AllReportsCategory key={c.title} category={c} defaultOpen={i === 0} />
            ))}
        </Stack>
    );
}

/* --------------------------------- shell --------------------------------- */

const TABS: { label: string; to: string; exact?: boolean }[] = [
    { label: 'All', to: '/bookkeeping/reports', exact: true },
    { label: 'Profit & Loss', to: '/bookkeeping/reports/profit-loss' },
    { label: 'Balance Sheet', to: '/bookkeeping/reports/balance-sheet' },
    { label: 'Cash Flow Statement', to: '/bookkeeping/reports/cash-flow-statement' },
    { label: 'Trial Balance', to: '/bookkeeping/reports/trial-balance' },
];

function ReportsTabBar() {
    const theme = useTheme();
    const location = useLocation();
    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Stack direction="row" spacing={6} sx={{ overflowX: 'auto' }}>
                {TABS.map((t) => {
                    const isActive = t.exact ? location.pathname === t.to : location.pathname.startsWith(t.to);
                    return (
                        <Box
                            key={t.to}
                            component={NavLink}
                            to={t.to}
                            sx={{
                                py: 1.5,
                                color: isActive ? 'text.primary' : 'text.secondary',
                                fontWeight: isActive ? 700 : 500,
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                                borderBottom: 2,
                                borderColor: isActive ? theme.palette.text.primary : 'transparent',
                                mb: '-1px',
                            }}>
                            {t.label}
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

export default function DemoReports() {
    const { data: member } = useMember();
    const entityType = getEntityType(member);
    const content = CONTENT[entityType] as { member?: { full_business_name?: string } };
    const businessName: string =
        content?.member?.full_business_name ??
        (member as { full_business_name?: string } | undefined)?.full_business_name ??
        'Demo Business LLC';

    return (
        <Box sx={{ width: '100%', maxWidth: 1280, mx: 'auto' }}>
            <Typography variant="h1" sx={{ mb: { xs: 4, md: 6 } }}>
                Reports
            </Typography>
            <ReportsTabBar />
            <Switch>
                <Route exact path="/bookkeeping/reports" render={() => <AllView />} />
                <Route
                    path="/bookkeeping/reports/profit-loss"
                    render={() => <ProfitLossView businessName={businessName} entityType={entityType} />}
                />
                <Route
                    path="/bookkeeping/reports/balance-sheet"
                    render={() => <BalanceSheetView businessName={businessName} entityType={entityType} />}
                />
                <Route
                    path="/bookkeeping/reports/cash-flow-statement"
                    render={() => <CashFlowView businessName={businessName} entityType={entityType} />}
                />
                <Route
                    path="/bookkeeping/reports/trial-balance"
                    render={() => <TrialBalanceView businessName={businessName} entityType={entityType} />}
                />
                <Redirect to="/bookkeeping/reports" />
            </Switch>
        </Box>
    );
}
